import React, {useMemo, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import 'core/yupCustomValidations';

import {Row} from 'react-table';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductsPackageType, ProductsStatus} from 'core/dictionaries';
import {
    aggregationForm$,
    aggregationFormChanged,
    stepChanged,
} from 'models/documents/create/aggregation';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    search$,
    searchChanged,
    tableState$,
} from 'models/documents/create/aggregation/tables/parentCodes';
import {
    AggregationParentCode,
    GridFieldConfig,
    ProductsGridAccessors,
    ProductsGridFormattedRow,
} from 'types';
import {PackageTypes} from 'types/gtins';
import {handleNil} from 'utils/handleNil';
import {mapArrayToI18nOptions} from 'utils/select';

import {HeadContainer, StepNavigationContainer} from '../common/styled';

import {CodeContainer, ModalWrapper, Wrapper} from './styled';

const getConfig = (t: TFunction): GridFieldConfig[] => [
    {
        id: 'select',
        header: false,
        hideHeader: true,
        width: 'auto',
    },
    {
        id: ProductsGridAccessors.code,
        header: t('pages.products.grid.code'),
    },
    {
        id: ProductsGridAccessors.packageType,
        header: t('pages.products.grid.package'),
        filter: (
            <Select
                isClearable
                placeholder={t('pages.products.grid.chooseOption')}
                options={mapArrayToI18nOptions(
                    [
                        PackageTypes.BOX,
                        PackageTypes.PALLET,
                        PackageTypes.CONTAINER,
                        PackageTypes.CUSTOMS_UNIT,
                    ].map((value) => ({key: value, value})),
                    'dictionaries.products.packageType',
                )}
            />
        ),
    },
    {
        id: ProductsGridAccessors.status,
        header: t('pages.products.grid.status'),
        maxWidth: 100,
    },
];

export function convertData(
    data: AggregationParentCode[] | undefined,
    t: TFunction,
): Partial<ProductsGridFormattedRow>[] {
    if (!data) return [];

    return data.map((elem) => ({
        [ProductsGridAccessors.code]: handleNil(elem.markingCode),
        [ProductsGridAccessors.status]: handleNil(elem.status, (value: ProductsStatus) => (
            <StatusWrapper color={PRODUCT_STATUS_COLORS[value ?? 'DEFAULT']}>
                {t(`dictionaries.products.status.${value}`)}
            </StatusWrapper>
        )),
        [ProductsGridAccessors.packageType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
    }));
}

export const AggregationDocumentParentCodes: React.FC = () => {
    const {t} = useTranslation();
    useGate(Gate);

    const aggregationForm = useStore(aggregationForm$);
    const search = useStore(search$);
    const [confirm, setConfirm] = useState(false);

    const {data, pageNumber, isLoading, filters} = useStore(tableState$);
    const columns = useMemo(
        () => mapTableColumns(getConfig(t), filters, filtersChanged, filtersReset),
        [t, filters],
    );
    const mappedData = React.useMemo(() => convertData(data?.data, t), [data, t]);

    const onNextStep = () => {
        const needConfirm = data?.data.find(
            (n) => n.markingCode === aggregationForm.aggregationNumber,
        )?.hasChildren;
        if (needConfirm) setConfirm(true);
        else stepChanged(2);
    };

    const isHighlighted = (row: Row<ProductsGridFormattedRow>) =>
        aggregationForm.aggregationNumber === row.original[ProductsGridAccessors.code];

    const onSelect = (row: Row<ProductsGridFormattedRow>) => {
        aggregationFormChanged({aggregationNumber: row.original[ProductsGridAccessors.code]});
    };

    return (
        <Wrapper>
            <HeadContainer>
                <CodeContainer>
                    <Input
                        label={t('pages.documents.form.aggregationCode')}
                        placeholder={t('pages.documents.codesSearch')}
                        value={search ?? undefined}
                        onChange={(e) => {
                            aggregationFormChanged({aggregationNumber: null});
                            searchChanged(e.target.value.replaceAll(' ', ''));
                        }}
                    />
                </CodeContainer>
                <StepNavigationContainer>
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Secondary}
                        onClick={() => stepChanged(0)}
                    >
                        {t('components.form.prevStep')}
                    </Button>
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        onClick={onNextStep}
                        disabled={
                            !aggregationForm.aggregationNumber || !data?.data.length || isLoading
                        }
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </StepNavigationContainer>
            </HeadContainer>
            <Table
                columns={columns}
                data={mappedData}
                hasNext={!!data?.nextPageRef}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                isLoading={isLoading}
                onRowClick={onSelect}
                highlightedRow={isHighlighted}
                hideSetting
            />
            <Modal
                show={confirm}
                onClose={() => setConfirm(false)}
                title={t('pages.documents.aggregation.hasChildrenWarning.title')}
                modalWidth={0}
            >
                <ModalWrapper>
                    <p>{t('pages.documents.aggregation.hasChildrenWarning.desc')}</p>
                    <div>
                        <Button
                            variant={ButtonVariant.Outline}
                            onClick={() => {
                                setConfirm(false);
                                stepChanged(2);
                            }}
                        >
                            {t('pages.documents.aggregation.hasChildrenWarning.buttons.continue')}
                        </Button>
                        <Button onClick={() => setConfirm(false)}>
                            {t('pages.documents.aggregation.hasChildrenWarning.buttons.another')}
                        </Button>
                    </div>
                </ModalWrapper>
            </Modal>
        </Wrapper>
    );
};
